import React, { useEffect, useState } from "react";
import HeaderPrint from "../../../Pdf/PrintHeaderAccount";
import axios from "axios";
import config from "../../../config";
import { calculateColumnTotal } from "../../Shared/Utility";
import { saleReportStyles } from "../../../Pdf/pdfStyles";
import PrintFooter from "../../../Pdf/PrintFooter";
import {
  PrintFormateNumber,
  parseReportAmount,
} from "../../../Pdf/PrintFormateNumber";
import {
  PDFViewer,
  Page,
  Text,
  View,
  Document,
} from "@react-pdf/renderer";
import moment from "moment";
import { Spin } from "antd";

const COL = {
  sr: 26,
  dated: 52,
  opening: 54,
  cashSales: 54,
  creditSales: 54,
  cashPurchase: 54,
  creditPurchase: 54,
  totalReceipts: 54,
  totalPayments: 54,
  totalExpenses: 54,
  closingCash: 57,
};

const TABLE_WIDTH = Object.values(COL).reduce((sum, w) => sum + w, 0);

const AMOUNT_KEYS = [
  "Opening_Cash",
  "Cash_Sales",
  "Credit_Sales",
  "Cash_Purchase",
  "Credit_Purchase",
  "Total_Receipts",
  "Total_Payments",
  "Total_Expenses",
  "Closing_Cash",
];

const fmt = (val) => PrintFormateNumber(parseReportAmount(val), 0);

const ThCell = ({ width, label, align }) => (
  <View style={[saleReportStyles.th, { width, textAlign: align || "left" }]}>
    <Text style={saleReportStyles.thValue}>{label}</Text>
  </View>
);

const TdCell = ({ width, value, align }) => (
  <View style={[saleReportStyles.td, { width, textAlign: align || "left" }]}>
    <Text style={saleReportStyles.tdValue}>{value ?? "—"}</Text>
  </View>
);

const PrintDateWiseSummaryAll = () => {
  const UserID = localStorage.getItem("ID");
  const AccessKey = localStorage.getItem("AccessKey");
  const urlParams = new URLSearchParams(window.location.search);

  const EndDate = urlParams.get("EndDate");
  const StartDate = urlParams.get("StartDate");

  const [dateWiseSummaryList, setDateWiseSummaryList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Print DateWise Summary";
    if (!StartDate || !EndDate || !UserID || !AccessKey) {
      setLoading(false);
      return;
    }

    setLoading(true);
    axios({
      method: "post",
      url: config.base_url + "Report/DateWiseSummaryReport",
      headers: { "Content-Type": "application/json" },
      data: {
        UserID,
        AccessKey,
        StartDate: moment(StartDate).isValid()
          ? moment(StartDate).format("YYYY-MM-DD")
          : StartDate,
        EndDate: moment(EndDate).isValid()
          ? moment(EndDate).format("YYYY-MM-DD")
          : EndDate,
      },
    })
      .then(function (response) {
        if (response.data.status_code == 1) {
          setDateWiseSummaryList(response.data.ListofSummary || []);
        } else {
          setDateWiseSummaryList([]);
        }
        setLoading(false);
      })
      .catch(function () {
        setDateWiseSummaryList([]);
        setLoading(false);
      });
  }, [StartDate, EndDate, UserID, AccessKey]);

  const periodLabel = `${
    StartDate && moment(StartDate).isValid()
      ? moment(StartDate).format(config.date_format)
      : "—"
  } to ${
    EndDate && moment(EndDate).isValid()
      ? moment(EndDate).format(config.date_format)
      : "—"
  }`;

  const totals = AMOUNT_KEYS.reduce((acc, key) => {
    if (key === "Opening_Cash" && dateWiseSummaryList.length > 0) {
      acc[key] = parseReportAmount(dateWiseSummaryList[0].Opening_Cash);
    } else if (key === "Closing_Cash" && dateWiseSummaryList.length > 0) {
      acc[key] = parseReportAmount(
        dateWiseSummaryList[dateWiseSummaryList.length - 1].Closing_Cash
      );
    } else {
      acc[key] = calculateColumnTotal(dateWiseSummaryList, key);
    }
    return acc;
  }, {});

  const generateData = () =>
    dateWiseSummaryList.map((item, index) => (
      <View key={index} style={[saleReportStyles.row, { width: TABLE_WIDTH }]}>
        <TdCell width={COL.sr} value={String(index + 1)} />
        <TdCell
          width={COL.dated}
          value={
            item.Dated
              ? moment(item.Dated).format(config.date_format)
              : "—"
          }
        />
        <TdCell width={COL.opening} value={fmt(item.Opening_Cash)} align="right" />
        <TdCell width={COL.cashSales} value={fmt(item.Cash_Sales)} align="right" />
        <TdCell
          width={COL.creditSales}
          value={fmt(item.Credit_Sales)}
          align="right"
        />
        <TdCell
          width={COL.cashPurchase}
          value={fmt(item.Cash_Purchase)}
          align="right"
        />
        <TdCell
          width={COL.creditPurchase}
          value={fmt(item.Credit_Purchase)}
          align="right"
        />
        <TdCell
          width={COL.totalReceipts}
          value={fmt(item.Total_Receipts)}
          align="right"
        />
        <TdCell
          width={COL.totalPayments}
          value={fmt(item.Total_Payments)}
          align="right"
        />
        <TdCell
          width={COL.totalExpenses}
          value={fmt(item.Total_Expenses)}
          align="right"
        />
        <TdCell
          width={COL.closingCash}
          value={fmt(item.Closing_Cash)}
          align="right"
        />
      </View>
    ));

  const MyDocument = () => (
    <Document>
      <Page size="A4" style={saleReportStyles.page} wrap>
        <HeaderPrint compact />
        <View style={saleReportStyles.section}>
          <View style={saleReportStyles.titleBlock}>
            <Text style={saleReportStyles.pagetitle}>DateWise Summary</Text>
            <Text style={saleReportStyles.reportSubtitle}>
              Period: {periodLabel}
            </Text>
          </View>

          <View style={[saleReportStyles.table, { width: TABLE_WIDTH }]}>
            <View style={[saleReportStyles.tableHead, { width: TABLE_WIDTH }]}>
              <ThCell width={COL.sr} label="Sr." />
              <ThCell width={COL.dated} label="Date" />
              <ThCell width={COL.opening} label="Opening Cash" align="right" />
              <ThCell width={COL.cashSales} label="Cash Sales" align="right" />
              <ThCell width={COL.creditSales} label="Credit Sales" align="right" />
              <ThCell width={COL.cashPurchase} label="Cash Purch." align="right" />
              <ThCell width={COL.creditPurchase} label="Credit Purch." align="right" />
              <ThCell width={COL.totalReceipts} label="Receipts" align="right" />
              <ThCell width={COL.totalPayments} label="Payments" align="right" />
              <ThCell width={COL.totalExpenses} label="Expenses" align="right" />
              <ThCell width={COL.closingCash} label="Closing Cash" align="right" />
            </View>

            {generateData()}

            {dateWiseSummaryList.length > 0 && (
              <View style={[saleReportStyles.totalsRow, { width: TABLE_WIDTH }]}>
                <View
                  style={[
                    saleReportStyles.td,
                    { width: COL.sr + COL.dated, textAlign: "right" },
                  ]}
                >
                  <Text style={saleReportStyles.thValue}>Total</Text>
                </View>
                <TdCell
                  width={COL.opening}
                  value={fmt(totals.Opening_Cash)}
                  align="right"
                />
                <TdCell
                  width={COL.cashSales}
                  value={fmt(totals.Cash_Sales)}
                  align="right"
                />
                <TdCell
                  width={COL.creditSales}
                  value={fmt(totals.Credit_Sales)}
                  align="right"
                />
                <TdCell
                  width={COL.cashPurchase}
                  value={fmt(totals.Cash_Purchase)}
                  align="right"
                />
                <TdCell
                  width={COL.creditPurchase}
                  value={fmt(totals.Credit_Purchase)}
                  align="right"
                />
                <TdCell
                  width={COL.totalReceipts}
                  value={fmt(totals.Total_Receipts)}
                  align="right"
                />
                <TdCell
                  width={COL.totalPayments}
                  value={fmt(totals.Total_Payments)}
                  align="right"
                />
                <TdCell
                  width={COL.totalExpenses}
                  value={fmt(totals.Total_Expenses)}
                  align="right"
                />
                <TdCell
                  width={COL.closingCash}
                  value={fmt(totals.Closing_Cash)}
                  align="right"
                />
              </View>
            )}
          </View>

          <View
            style={{ marginBottom: 20, marginTop: 0, top: 6, bottom: 6 }}
            fixed
          >
            <Text
              style={{ fontSize: "7pt", color: "#000" }}
              render={({ pageNumber, totalPages }) =>
                `Page ${pageNumber} / ${totalPages}`
              }
            />
          </View>
        </View>
        <PrintFooter fixed />
      </Page>
    </Document>
  );

  return (
    <div style={{ width: "100%", height: "100vh", overflow: "hidden" }}>
      {loading ? (
        <div
          style={{
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spin />
        </div>
      ) : (
        <PDFViewer width="100%" height="100%" fileName="DateWiseSummary.pdf">
          <MyDocument />
        </PDFViewer>
      )}
    </div>
  );
};

export default PrintDateWiseSummaryAll;
